import { apiService } from 'core/services/apiService';
import { paginationService } from 'core/services/paginationService';
import {
  ShiftRequest,
  ShiftRequestsResponse,
  ShiftRequestTypesResponse,
  ShiftRequestType,
  CreateShiftRequestPayload,
} from 'types/shift-requests';

// Get all shift requests
export const getShiftRequests = async (params?: {
  workplace_id?: number;
  employee_id?: number;
  page?: number;
  limit?: number;
}): Promise<ShiftRequestsResponse> => {
  const queryParams = new URLSearchParams();

  if (params?.workplace_id) queryParams.append('workplace', params.workplace_id.toString());
  if (params?.employee_id) queryParams.append('employeeRequest', params.employee_id.toString());

  const endpoint = queryParams.toString() 
    ? `shift_requests?${queryParams.toString()}`
    : 'shift_requests';

  const itemsPerPage = params?.limit || 30;
  const data = await paginationService.getWithHandler(endpoint, undefined, itemsPerPage);
  return data;
};

// Get single shift request by ID
export const getShiftRequest = async (requestId: number): Promise<ShiftRequest> => {
  const data = apiService.responseHandler(
    await apiService.get(`shift_requests/${requestId}`)
  );
  return data;
};

// Create a new shift request
export const createShiftRequest = async (
  payload: CreateShiftRequestPayload
): Promise<ShiftRequest> => {
  // Determine endpoint based on requestType parameter
  let endpoint = 'shift_requests';
  
  if (payload.requestType) {
    switch (payload.requestType) {
      case 'availability':
        endpoint = 'shift_requests/availability';
        break;
      case 'time-off':
        endpoint = 'shift_requests/time-off';
        break;
      case 'cover':
        endpoint = 'shift_requests/cover';
        break;
      case 'pickup':
        endpoint = 'shift_requests/pickup';
        break;
      case 'trade':
        endpoint = 'shift_requests/trade';
        break;
    }
  }
  
  // Remove requestType from payload before sending to API
  const { requestType, ...apiPayload } = payload;
  
  const data = apiService.responseHandler(
    await apiService.post(endpoint, apiPayload)
  );
  return data;
};

// Update a shift request
export const updateShiftRequest = async (
  requestId: number,
  payload: Partial<CreateShiftRequestPayload>
): Promise<ShiftRequest> => {
  const data = apiService.responseHandler(
    await apiService.put(`shift_requests/${requestId}`, payload)
  );
  return data;
};

// Delete a shift request
export const deleteShiftRequest = async (requestId: number): Promise<void> => {
  await apiService.delete(`shift_requests/${requestId}`);
};

// Approve or deny a shift request using type-specific endpoints
export const approveOrDenyShiftRequest = async (
  requestId: number,
  requestType: string,
  approve: boolean
): Promise<ShiftRequest> => {
  const payload = {
    employeeRequestConfirmed: approve ? true : false
  };

  // Determine endpoint based on request type
  let endpoint = `shift_requests/${requestId}`;
  
  if (requestType) {
    const typeLower = requestType.toLowerCase();
    if (typeLower.includes('availability')) {
      endpoint = `shift_requests/availability/${requestId}`;
    } else if (typeLower.includes('time off') || typeLower.includes('time-off')) {
      endpoint = `shift_requests/time-off/${requestId}`;
    } else if (typeLower.includes('cover')) {
      endpoint = `shift_requests/cover/${requestId}`;
    } else if (typeLower.includes('pickup') || typeLower.includes('pick up')) {
      endpoint = `shift_requests/pickup/${requestId}`;
    } else if (typeLower.includes('trade')) {
      endpoint = `shift_requests/trade/${requestId}`;
    }
  }
  
  const data = apiService.responseHandler(
    await apiService.put(endpoint, payload)
  );
  return data;
};

// Get all shift request types
export const getShiftRequestTypes = async (): Promise<ShiftRequestTypesResponse> => {
  const data = await paginationService.getWithHandler('shift_request_types');
  const types = data?.member || data?.['hydra:member'] || [];
  
  return {
    '@context': data['@context'] || '/api/contexts/ShiftRequestType',
    '@id': data['@id'] || '/api/shift_request_types',
    '@type': data['@type'] || 'Collection',
    'totalItems': data.totalItems || types.length,
    'member': types
  };
};
